import { memo, useEffect, useMemo, useRef, useState } from 'react';
import AssistantButton from './Button';
import AssistantDashboard from './Dashboard';
import { AssistantProvider } from './providers/Provider';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers/MytaverseEventProvider';
import { useUserName } from './providers/UserName';
import { log } from './providers/Debug';
import { env } from 'env';

const { REACT_APP_ENABLE_ASSISTANT_ON_EVENTS_PAGE } = env;
const REGION_EXIT_DEBOUNCE_MS = 1000;

const AssistantWrapper = memo(
  ({ isSelectEventPage }: { isSelectEventPage: boolean }) => {
    if (
      (REACT_APP_ENABLE_ASSISTANT_ON_EVENTS_PAGE === 'true' &&
        isSelectEventPage) ||
      !isSelectEventPage
    ) {
      return (
        <>
          <AssistantButton isLargeButton={isSelectEventPage} />
          <AssistantDashboard />
        </>
      );
    }

    return null;
  },
);

const Assistant = ({ isSelectEventPage }: { isSelectEventPage?: boolean }) => {
  const mytaverseEvent = useMytaverseEvent();
  const mytaverseEventRef = useRef(mytaverseEvent ?? {});
  mytaverseEventRef.current = mytaverseEvent ?? {};

  const { sendJSONMessageToWebSocket, user, selectedLanguage } = useMytaverse();
  const sendWSRef = useRef(sendJSONMessageToWebSocket);
  sendWSRef.current = sendJSONMessageToWebSocket;
  const mytaverseUserRef = useRef(user ?? {});

  const selectedLanguageRef = useRef(selectedLanguage);
  selectedLanguageRef.current = selectedLanguage;

  const userName = useUserName();

  const assistantScriptUpdateTick = mytaverseEvent?.assistantScriptUpdateTick;
  const activeBrandingId = mytaverseEvent?.currentRoom?.activeBrandingId;
  const rawRegion = mytaverseEvent?.currentRegion?.region;
  const aiTriggerName = mytaverseEvent?.aiTriggerName;
  const rawObjectPoiId = mytaverseEvent?.objectPoiId;
  const objectPoiId = rawObjectPoiId !== undefined ? rawObjectPoiId : '';
  const [currentRegionName, setCurrentRegionName] = useState<
    string | undefined
  >(rawRegion ?? aiTriggerName ?? undefined);
  const currentRegionNameRef = useRef(currentRegionName);
  const clearRegionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  currentRegionNameRef.current = currentRegionName;

  useEffect(() => {
    if (clearRegionTimeoutRef.current) {
      clearTimeout(clearRegionTimeoutRef.current);
      clearRegionTimeoutRef.current = null;
    }

    if (rawRegion) {
      setCurrentRegionName(rawRegion);
      return;
    }

    if (aiTriggerName) {
      setCurrentRegionName(aiTriggerName);
      return;
    }

    log('[region] debounce started', { rawRegion, aiTriggerName });
    clearRegionTimeoutRef.current = setTimeout(() => {
      clearRegionTimeoutRef.current = null;
      log('[region] debounce finished — clearing region');
      setCurrentRegionName(undefined);
    }, REGION_EXIT_DEBOUNCE_MS);

    return () => {
      if (clearRegionTimeoutRef.current) {
        clearTimeout(clearRegionTimeoutRef.current);
        clearRegionTimeoutRef.current = null;
      }
    };
  }, [aiTriggerName, rawRegion]);

  const children = useMemo(
    () => <AssistantWrapper isSelectEventPage={isSelectEventPage || false} />,
    [isSelectEventPage],
  );

  return (
    <AssistantProvider
      currentRegionName={currentRegionName}
      mytaverseUserRef={mytaverseUserRef}
      userName={userName}
      mytaverseEventRef={mytaverseEventRef}
      sendWSRef={sendWSRef}
      selectedLanguageRef={selectedLanguageRef}
      objectPoiId={objectPoiId}
      assistantScriptUpdateTick={assistantScriptUpdateTick}
      activeBrandingId={activeBrandingId}
    >
      {children}
    </AssistantProvider>
  );
};

export default Assistant;
